import { APIEvent } from "./api/event";
import { plainToClassFromExist, plainToInstance } from "class-transformer";
import { ResponseModel } from "@/models/classes/response";
import { ResponsesModel } from "@/models/classes/responses";
import { MasterMaterialModel } from "@/models/classes/event/master_material";
import { EventDetailModel } from "@/models/classes/event/event_detail";
import { EventListModel } from "@/models/classes/event/event_list";
import { TechnicianModel } from "@/models/classes/event/technician";
import {
  OnEventModel,
  UpdateOnEventDetailModel,
  UpdateOnEventInstallationModel,
} from "@/models/classes/event/on_event";
import { EventDetailInstallModel } from "@/models/classes/event/event_detail_install";
import APIFile from "@/services/api/file";

const api = new APIEvent();

export class EventService {
  // COMMON
  async getActionStatus() {
    const resData = await api.getActionStatus();
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  async getEventList(params: any) {
    const resData = await api.getEventList(params);
    const resModel = plainToClassFromExist(
      new ResponsesModel<EventListModel>(EventListModel),
      resData,
    );
    if (resModel.status === 200 && typeof resModel.data === "object") {
      return {
        data: resModel.data,
        total: resModel.total || resModel.data?.length || 0,
      };
    }
    return { data: [], total: 0 };
  }

  async getEventMasterMaterialGroup(matType?: string) {
    const resData = await api.getEventMasterMaterialGroup(matType);
    if (resData && resData.result && resData.data) {
      return resData.data;
    }
    return undefined;
  }

  async getEventMasterMaterial(
    matType: string,
    searchText: string,
    matGroupId?: number,
    matBrandId?: number,
    page: number = 1,
    limit: number = 20,
  ) {
    const resData = await api.getEventMasterMaterial(
      matType,
      searchText,
      matGroupId,
      matBrandId,
      page,
      limit,
    );

    const resModel = plainToClassFromExist(
      new ResponsesModel<MasterMaterialModel>(MasterMaterialModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return {
        data: resModel.data || [],
        total: resModel.total || (resModel.data || []).length,
      };
    }
    return undefined;
  }

  async getTechnician(
    eventId: number,
    searchText: string,
    offset: number = 0,
    limit: number = 20,
  ) {
    const resData = await api.getTechnician(
      eventId,
      searchText,
      offset,
      limit,
    );
    const resModel = plainToClassFromExist(
      new ResponsesModel<TechnicianModel>(TechnicianModel),
      resData,
    );
    if (resModel.status == 200 && Array.isArray(resModel.data)) {
      return {
        data: resModel.data,
        result: resModel.result,
        total: resModel.total || resModel.data.length,
      };
    }
    return { data: [], result: true, total: 0 };
  }


  async approveEvent(payload: { events: any[]; isApproved: boolean }) {
    const resData = await api.approveEvent(payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  // PRE-EVENT
  async getPreEventDetail(eventId: number) {
    const resData = await api.getPreEventDetail(eventId);
    const resModel = plainToClassFromExist(
      new ResponseModel<EventDetailModel>(EventDetailModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data === "object") {
      return {
        data: resModel.data,
        result: resModel.result,
      };
    }
    return undefined;
  }

  async updatePreEventDetail(customerId: number, localEvent: EventDetailModel) {
    const payload = {
      ...localEvent,
      eventId: customerId || localEvent.eventId || 0,
    };

    const resData = await api.updatePreEventDetail(payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  async updatePreEventDelivery(eventId: number, payload: any) {
    const resData = await api.updatePreEventDelivery(eventId, payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  async addPreEventTechnician(
    eventId: number,
    payload: {
      isApproved: boolean;
      technician: { contactId: number[] };
      actualAmountTechnician?: number;
      detail?: string;
    },
  ) {
    const resData = await api.addPreEventTechnician(eventId, payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  async customerPreEventAccept(
    eventId: number,
    payload: { isAccepted: boolean; reason?: string },
  ) {
    const resData = await api.customerPreEventAccept(eventId, payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  async uploadPreEventReceipt(
    eventId: number,
    payload: {
      imgUrls: string[];
      paymentType: string;
      depositAmount: number;
      note?: string;
      product?: any[];
    },
  ) {
    const resData = await api.uploadPreEventReceipt(eventId, payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }


  // ON-EVENT
  async getOnEvent(eventId: number) {
    const resData = await api.getOnEvent(eventId);
    const resModel = plainToClassFromExist(
      new ResponseModel<OnEventModel>(OnEventModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data === "object") {
      return {
        data: resModel.data,
        result: resModel.result,
      };
    }
    return undefined;
  }

  async getOnEventPayment(eventId: number) {
    const resData = await api.getOnEventPayment(eventId);
    const resModel = plainToClassFromExist(
      new ResponseModel<EventDetailModel>(EventDetailModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data === "object") {
      return {
        data: resModel.data,
        result: resModel.result,
      };
    }
    return undefined;
  }

  async updateOnEventPayment(
    eventId: number,
    payload: UpdateOnEventDetailModel,
  ) {
    const resData = await api.updateOnEventPayment(eventId, payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  async getOnEventInstallation(eventId: number) {
    const resData = await api.getOnEventInstallation(eventId);
    const resModel = plainToClassFromExist(
      new ResponseModel<EventDetailInstallModel>(EventDetailInstallModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data === "object") {
      return {
        data: resModel.data,
        result: resModel.result,
      };
    }
    return undefined;
  }

  async updateOnEventInstallation(
    eventId: number,
    payload: UpdateOnEventInstallationModel,
  ) {
    const resData = await api.updateOnEventInstallation(eventId, payload);
    if (resData && resData.result) {
      return {
        data: resData.data,
        result: resData.result,
      };
    }
    return undefined;
  }

  // POST-EVENT
}
